import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventContext = createContext(null);

export function EventProvider({ children }) {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createEvent = useCallback(async (eventData) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/events`, eventData, { headers });
      const newEvent = response.data.event;
      setCurrentEvent(newEvent);
      setEvents(prev => [newEvent, ...prev]);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to create event';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvent = useCallback(async (eventId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setCurrentEvent(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to fetch event';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to fetch events';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateMatch = useCallback(async (eventId, matchId, updateData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${API}/events/${eventId}/schedule/${matchId}`, updateData);
      // Refresh event data
      await fetchEvent(eventId);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to update match';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [fetchEvent]);

  const regenerateSchedule = useCallback(async (eventId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/events/${eventId}/regenerate`);
      await fetchEvent(eventId);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to regenerate schedule';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [fetchEvent]);

  // Manually add an extra match to an event (Pro-only). Used for rain
  // make-ups, friendlies, or filling in playoff brackets that didn't
  // auto-generate.
  const addMatch = useCallback(async (eventId, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/events/${eventId}/schedule`, payload);
      await fetchEvent(eventId);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to add match';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [fetchEvent]);

  // Delete a match (Pro-only). The backend requires confirm=DELETE as a
  // safeguard — the UI dialog forces users to type it before enabling
  // the delete button.
  const deleteMatch = useCallback(async (eventId, matchId) => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`${API}/events/${eventId}/schedule/${matchId}?confirm=DELETE`);
      await fetchEvent(eventId);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to delete match';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [fetchEvent]);

  // Bulk-delete matches from a schedule (Pro-only). Same typed-DELETE
  // safeguard as the single-match endpoint — the UI dialog forces the
  // user to type DELETE before this fires. Returns the actual number
  // deleted so the caller can toast the true count.
  const deleteMatchesBulk = useCallback(async (eventId, matchIds) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/events/${eventId}/schedule/bulk-delete`,
        { match_ids: matchIds, confirm: 'DELETE' },
      );
      await fetchEvent(eventId);
      return res.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to delete matches';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [fetchEvent]);

  const deleteEvent = useCallback(async (eventId) => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`${API}/events/${eventId}`);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      if (currentEvent?.id === eventId) {
        setCurrentEvent(null);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to delete event';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [currentEvent]);

  const getExportUrl = useCallback((eventId, format, options = {}) => {
    const baseUrl = `${API}/events/${eventId}/export/${format}`;
    if (format === 'pdf' && options.orientation) {
      return `${baseUrl}?orientation=${options.orientation}`;
    }
    return baseUrl;
  }, []);

  const value = {
    currentEvent,
    setCurrentEvent,
    events,
    loading,
    error,
    createEvent,
    fetchEvent,
    fetchEvents,
    updateMatch,
    addMatch,
    deleteMatch,
    deleteMatchesBulk,
    regenerateSchedule,
    deleteEvent,
    getExportUrl,
  };

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
}

export function useEvent() {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within an EventProvider');
  }
  return context;
}
